import { useEffect, useState } from "react";
import { opsAPI } from "./opsApi";
import { Sparkles, Send, X, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function AskAssistant() {
  const [open, setOpen] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && !questions.length) {
      opsAPI.aiQuestions().then((d) => setQuestions(d.questions || [])).catch(() => {});
    }
  }, [open, questions.length]);

  const ask = async (q) => {
    setLoading(true);
    setHistory((h) => [...h, { role: "user", text: q.label }]);
    try {
      const res = await opsAPI.aiAnswer(q.id);
      setHistory((h) => [...h, {
        role: "assistant",
        text: res.answer,
        evidence: res.evidence || [],
      }]);
    } catch (e) {
      toast.error("Failed to answer");
    } finally { setLoading(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="ask-assistant-launcher"
        className="fixed bottom-6 right-6 z-40 h-14 pl-4 pr-5 rounded-full shadow-2xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-amber-400 text-white font-medium inline-flex items-center gap-2.5 hover:shadow-[0_20px_60px_rgba(217,70,239,0.35)] transition-shadow"
      >
        <Sparkles className="w-5 h-5" strokeWidth={2}/>
        <span className="text-[13.5px]">Ask AI</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:justify-end p-4 sm:p-6" onClick={() => setOpen(false)}>
          <div
            data-testid="ask-assistant-panel"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md h-[85vh] sm:h-[640px] bg-[#0B1020] border border-white/[0.08] rounded-3xl overflow-hidden flex flex-col shadow-2xl"
          >
            <div className="px-5 py-4 border-b border-white/[0.08] flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" strokeWidth={2}/>
              </div>
              <div className="flex-1">
                <div className="font-display text-[15px] font-semibold text-white">AI Assistant</div>
                <div className="text-[11px] text-white/50">Deterministic · Evidence-backed</div>
              </div>
              <button onClick={() => setOpen(false)} className="p-2 rounded-lg text-white/60 hover:bg-white/5"><X className="w-4 h-4"/></button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {history.length === 0 && (
                <div className="text-center py-6">
                  <MessageCircle className="w-8 h-8 text-white/30 mx-auto mb-2"/>
                  <div className="text-[13px] text-white/60">Pick a question below to start.</div>
                </div>
              )}
              {history.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] ${
                    m.role === "user"
                      ? "bg-white/10 text-white"
                      : "bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 border border-violet-500/30 text-white"}`}>
                    {m.text}
                    {m.evidence?.length ? (
                      <div className="mt-2 pt-2 border-t border-white/10 space-y-1">
                        {m.evidence.slice(0, 4).map((e, j) => (
                          <div key={j} className="text-[11px] text-white/60 mono truncate">
                            {Object.entries(e).map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ")}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] bg-white/5 text-white/60">
                    <span className="inline-block animate-pulse">Analysing…</span>
                  </div>
                </div>
              )}
            </div>

            <div className="p-3 border-t border-white/[0.08] bg-white/[0.02]">
              <div className="text-[10.5px] uppercase tracking-widest text-white/40 font-semibold mb-2 px-1">Try asking</div>
              <div className="grid grid-cols-1 gap-1.5">
                {questions.map((q) => (
                  <button
                    key={q.id}
                    onClick={() => ask(q)}
                    data-testid={`ask-question-${q.id}`}
                    disabled={loading}
                    className="text-left px-3 py-2 rounded-lg text-[12.5px] text-white/80 bg-white/[0.03] hover:bg-white/[0.08] border border-white/[0.05] disabled:opacity-50 transition-colors inline-flex items-center gap-2"
                  >
                    <Send className="w-3.5 h-3.5 text-violet-300 shrink-0"/>
                    <span className="truncate">{q.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
